import { create } from "zustand";

import type { EdgeOut, Neighborhood, NodeOut, NodeType } from "@/lib/api";

/** force-graph mutates node objects with simulation coords — keep identity. */
export type GNode = NodeOut & { x?: number; y?: number; fx?: number; fy?: number };

export interface GLink {
  id: string;
  source: string | GNode;
  target: string | GNode;
  edge: EdgeOut;
}

export type Selection =
  | { kind: "node"; id: string }
  | { kind: "edge"; id: string }
  | null;

function sameSelection(a: Selection, b: Selection): boolean {
  return !!a && !!b && a.kind === b.kind && a.id === b.id;
}

interface GraphState {
  nodes: Map<string, GNode>;
  links: Map<string, GLink>;
  version: number;
  selection: Selection;
  hoverNodeId: string | null;
  expanded: Set<string>;
  /** node ids that should flash (violet pulse, the --primary token; the brass
   * alias was removed), with the time they were marked */
  flash: Map<string, number>;
  focusRequest: { nodeId: string; ts: number } | null;
  /** rubber-band multi-selection of node ids */
  multiSelected: Set<string>;
  /** node-type categories hidden from the canvas (top-right legend toggle) */
  hiddenTypes: Set<NodeType>;
  /**
   * Edge types hidden from the canvas — the other half of graph construction.
   *
   * Hiding a node type answers "what is in my graph". Hiding an edge type
   * answers "why are these things connected", and until now there was no way
   * to ask it: a vault of 200 files in 12 folders draws a `co_located` edge
   * for every pair in every folder, and that metadata mesh buries the handful
   * of links the user actually drew. Turning it off is not a cosmetic filter,
   * it is how you read your own graph.
   */
  hiddenEdgeTypes: Set<string>;
  /**
   * Floor for INFERRED links, in [0, 1]. An edge the model guessed at 0.31 and
   * one it is sure of at 0.98 used to paint identically; this is the dial that
   * separates them. Deterministic edges carry `confidence: null` and are never
   * filtered by it — a threshold on a thing with no confidence is meaningless.
   */
  minConfidence: number;
  /** bumped by the Del key so the inspector / bulk bar open their confirm */
  deleteIntent: number;

  setDocuments: (docs: NodeOut[]) => void;
  mergeNeighborhood: (nb: Neighborhood) => void;
  mergeBatch: (nodes: NodeOut[], edges: EdgeOut[]) => string[];
  select: (sel: Selection) => void;
  /** Click-to-toggle: selecting the already-selected item clears it. */
  toggleSelect: (sel: Selection) => void;
  setHover: (id: string | null) => void;
  requestFocus: (nodeId: string) => void;
  clearFocus: () => void;
  markFlash: (ids: string[]) => void;
  confirmEdgeLocal: (edgeId: string) => void;
  setMultiSelected: (ids: Set<string>) => void;
  clearMultiSelected: () => void;
  /** Hide/show a whole node-type category on the canvas (re-click to toggle). */
  toggleType: (type: NodeType) => void;
  /** Hide/show a whole relationship on the canvas (re-click to toggle). */
  toggleEdgeType: (type: string) => void;
  setMinConfidence: (v: number) => void;
  /** Back to "show everything" in one click — the escape hatch from a filter
   *  set you no longer remember building. */
  resetGraphFilters: () => void;
  requestDelete: () => void;
  removeNode: (id: string) => void;
  removeEdge: (id: string) => void;
}

function upsertNode(map: Map<string, GNode>, n: NodeOut): boolean {
  const prior = map.get(n.id);
  if (prior) {
    // Preserve simulation coords; refresh metadata.
    Object.assign(prior, n, { x: prior.x, y: prior.y });
    return false;
  }
  map.set(n.id, { ...n });
  return true;
}

export const useGraph = create<GraphState>((set, get) => ({
  nodes: new Map(),
  links: new Map(),
  version: 0,
  selection: null,
  hoverNodeId: null,
  expanded: new Set(),
  flash: new Map(),
  focusRequest: null,
  multiSelected: new Set(),
  hiddenTypes: new Set(),
  hiddenEdgeTypes: new Set(),
  minConfidence: 0,
  deleteIntent: 0,

  setMultiSelected: (ids) => set({ multiSelected: ids }),
  clearMultiSelected: () => set({ multiSelected: new Set() }),
  toggleType: (type) =>
    set((s) => {
      const hiddenTypes = new Set(s.hiddenTypes);
      if (hiddenTypes.has(type)) hiddenTypes.delete(type);
      else hiddenTypes.add(type);
      return { hiddenTypes };
    }),
  toggleEdgeType: (type) =>
    set((s) => {
      const hiddenEdgeTypes = new Set(s.hiddenEdgeTypes);
      if (hiddenEdgeTypes.has(type)) hiddenEdgeTypes.delete(type);
      else hiddenEdgeTypes.add(type);
      return { hiddenEdgeTypes };
    }),
  setMinConfidence: (v) => set({ minConfidence: Math.min(1, Math.max(0, v)) }),
  resetGraphFilters: () =>
    set({ hiddenTypes: new Set(), hiddenEdgeTypes: new Set(), minConfidence: 0 }),
  requestDelete: () => set((s) => ({ deleteIntent: s.deleteIntent + 1 })),

  removeNode: (id) => {
    const nodes = new Map(get().nodes);
    const links = new Map(get().links);
    nodes.delete(id);
    const removedEdges = new Set<string>();
    for (const [lid, l] of links) {
      const s = typeof l.source === "object" ? l.source.id : l.source;
      const t = typeof l.target === "object" ? l.target.id : l.target;
      if (s === id || t === id) {
        links.delete(lid);
        removedEdges.add(lid);
      }
    }
    const sel = get().selection;
    // Clear the selection if it was this node OR one of the edges that went
    // with it — otherwise an edge selection would dangle at a deleted edge.
    const selectionGone =
      (sel?.kind === "node" && sel.id === id) ||
      (sel?.kind === "edge" && removedEdges.has(sel.id));
    set({
      nodes,
      links,
      selection: selectionGone ? null : sel,
      version: get().version + 1,
    });
  },

  removeEdge: (id) => {
    const links = new Map(get().links);
    links.delete(id);
    const sel = get().selection;
    set({
      links,
      selection: sel?.kind === "edge" && sel.id === id ? null : sel,
      version: get().version + 1,
    });
  },

  setDocuments: (docs) => {
    const { nodes } = get();
    let changed = false;
    for (const d of docs) changed = upsertNode(nodes, d) || changed;
    if (changed) set((s) => ({ version: s.version + 1 }));
  },

  mergeNeighborhood: (nb) => {
    const fresh = get().mergeBatch([nb.center, ...nb.nodes], nb.edges);
    const expanded = new Set(get().expanded);
    expanded.add(nb.center.id);
    set({ expanded });
    if (fresh.length) get().markFlash(fresh);
  },

  mergeBatch: (ns, es) => {
    const { nodes, links } = get();
    const freshIds: string[] = [];
    for (const n of ns) {
      if (upsertNode(nodes, n)) freshIds.push(n.id);
    }
    for (const e of es) {
      if (!links.has(e.id) && nodes.has(e.src_id) && nodes.has(e.dst_id)) {
        links.set(e.id, { id: e.id, source: e.src_id, target: e.dst_id, edge: e });
      } else if (links.has(e.id)) {
        links.get(e.id)!.edge = e;
      }
    }
    set((s) => ({ version: s.version + 1 }));
    return freshIds;
  },

  select: (selection) => set({ selection }),
  toggleSelect: (sel) =>
    set((s) => ({ selection: sameSelection(s.selection, sel) ? null : sel })),
  setHover: (hoverNodeId) => set({ hoverNodeId }),
  requestFocus: (nodeId) => set({ focusRequest: { nodeId, ts: Date.now() } }),
  clearFocus: () => set({ focusRequest: null }),

  markFlash: (ids) => {
    const flash = new Map(get().flash);
    const now = Date.now();
    for (const id of ids) flash.set(id, now);
    set({ flash });
    // Prune after the pulse so paint stays cheap.
    setTimeout(() => {
      const f = new Map(get().flash);
      for (const id of ids) if ((f.get(id) ?? 0) === now) f.delete(id);
      set({ flash: f });
    }, 2600);
  },

  confirmEdgeLocal: (edgeId) => {
    const link = get().links.get(edgeId);
    if (link) {
      link.edge = { ...link.edge, status: "confirmed" };
      set((s) => ({ version: s.version + 1 }));
    }
  },
}));
